import React, {useRef, useEffect, useState} from 'react';
import _ from 'lodash';
import classnames from 'classnames/bind';
import style from './index.module.scss';
import OilThumb from '../OilThumb';
import {IProd, IState, ProductCategory, Replacement} from '../../../interface';
import localesReplacement from '../../../utils/localesReplacement';
import {connect} from 'react-redux';
import DOMPurify from 'dompurify';
import { useAnalytics } from '../../../hooks/use-analytics';
import { ANALYTICS_BUTTON_NAME, ANALYTICS_EVENT, ANALYTICS_PAGE_TYPE, ANALYTICS_RECOMMENDATION_TYPE } from '../../../constants';

const cx = classnames.bind(style);

interface IProps {
  dataList: Partial<IProd>;
  withoutMoreLink: boolean;
  replacements: Replacement;
  moreLinkClick?: () => void;
  productCategories: ProductCategory[];
  openModalHandle?: () => void;
  withTitle?: boolean;
  vehicle?: any;
  products?: any;
  componentName?: any;
  onPrintClick?: any;
  withoutLinks?: boolean;
  className?: string;
}

export const OilRecommendationInformation = (props: IProps) => {
  const {
    withoutMoreLink,
    dataList,
    replacements,
    moreLinkClick,
    openModalHandle,
    withTitle,
    vehicle,
    componentName,
    onPrintClick,
    withoutLinks,
    className,
  } = props;
  const [textOverflow, setTextOverflow] = useState<boolean>(false);
  const [textExpanded, setTextExpanded] = useState<boolean>(false);

  const [ height, setHeight ] = useState<number>(0);
  
  const sendAnalytics = useAnalytics();

  const ref = useRef<HTMLDivElement>(null);
  const readMoreDataLayer = () => {
    sendAnalytics({
      event: ANALYTICS_EVENT.READ_RECOMMENDED_PRODUCT_CLICKS ,
      page: ANALYTICS_PAGE_TYPE.RECOMMENDED_OILS,
      recommendation: ANALYTICS_RECOMMENDATION_TYPE.ALTERNATIVE_RECOMMENDATION,
      button: ANALYTICS_BUTTON_NAME.READ,
      oilName: dataList?.name,
      oilCategory: componentName,
      model: `${vehicle?.make} ${vehicle?.model}`,
      country: localStorage.getItem('countryCode') || '',
    });
  };
  useEffect(() => {
    const elem: any = ref?.current;
    setHeight(elem.clientHeight);
    if (elem && elem.scrollHeight > elem.clientHeight) {
      setTextOverflow(true);
    } else {
      setTextOverflow(false);
    }
  }, [dataList]);

  const handleMore = () => {
    const elem: any = ref?.current;
    if (!textExpanded) {
      requestAnimationFrame(() => {
        elem.style.removeProperty('height');
        elem.style.maxHeight = `${height}px`;
        requestAnimationFrame(() => {
          elem.style.maxHeight = `${elem.scrollHeight}px`;
        })            
      });                
      setTextExpanded(true);
    } else {
      requestAnimationFrame(() => {
        elem.style.height = `${elem.scrollHeight}px`;
        elem.style.overflow = 'hidden';
        requestAnimationFrame(() => {
          elem.style.height = `${height}px`;
          elem.style.removeProperty('max-height');
        })
      })
      setTextExpanded(false);
    }    
  }

  return (
    <div
      className={`oil-base ${cx('block')} ${cx(
        withTitle ? 'without-title' : '',
      )} ${className}`}
    >
      <div className={cx('thumb')}>
        <OilThumb
          withoutMoreLink={withoutMoreLink}
          dataList={dataList}
          moreLinkClick={moreLinkClick}
          onPrintClick={onPrintClick}
          withoutLinks={withoutLinks}
          maxSize={104}
        />
      </div>
      <div>
        {!withTitle && <h4 className={cx('name')}>{dataList['name']}</h4>}
        <div
          ref={ref}
          className={`${cx('desc')} ${
            textExpanded ? cx('no-clamp') : cx('clamp')
          }`}
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(_.get(dataList, 'description') || ''),
          }}
        ></div>
        {openModalHandle && (
          <span
            className='primary-link read-more'
            role='link'
            onClick={() => {
              openModalHandle();
              readMoreDataLayer();
            }}
          >
            {localesReplacement(replacements, 'Read more')}
          </span>
        )}
        {!openModalHandle && textOverflow && (
          <span
            className={`primary-link ${cx('read-more')}`}
            role='link'
            onClick={handleMore}
          >
            {localesReplacement(
              replacements,
              !textExpanded ? 'Read more' : 'Read less',
            )}
          </span>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: IState) => ({
  replacements: state.replacements,
});

export default connect(mapStateToProps)(OilRecommendationInformation);
